// Shared menu-line, menu-variant and dietary-variant styling for Ordo menu
// surfaces. ONE mapping — no scattered emoji literals or per-surface color
// picks.
//
// Color keys name entries in the Ordo light-badge palette (the client's
// `ordoLightBadgeClassName` / SIDEBAR_TILE_COLORS keys). They are declared
// here as a string union so this package stays dependency-free; every key
// below is a valid `OrdoLightColorKey` on the client.
//
// MENU LINES render as small COLOR CHIPS (label tinted per line — no emoji;
// product decision 2026-07-19). MENU VARIANTS render as EMOJI-prefixed labels
// (🍽️ Standard, 🐻 California, …). Dietary colors deliberately reuse the
// filterable-tag color system (contains-tag-summary-chips.tsx): teal Halal,
// indigo Kosher, cyan for "X-free" absence claims, violet for other custom
// dietary tags.

import {
  MENU_DIETARY_NEED_LABELS,
  MENU_LINE_LABELS,
  MENU_VARIANT_LABELS,
  type MenuBucketCode,
  type MenuDietaryNeed,
  type MenuLineCode,
  type MenuVariant,
} from './menu-taxonomy'

/** Subset of the client's OrdoLightColorKey palette used by menu styling. */
export type MenuBadgeColorKey =
  | 'red'
  | 'orange'
  | 'amber'
  | 'yellow'
  | 'green'
  | 'teal'
  | 'cyan'
  | 'blue'
  | 'indigo'
  | 'violet'
  | 'purple'
  | 'pink'
  | 'rose'
  | 'slate'
  | 'gray'

/**
 * Solid hex per badge color key — the Tailwind 600 step of each Ordo light
 * tile color. For surfaces that store a color VALUE rather than a key (the
 * menus `Tag.color` column, print styles); keys and hexes MUST stay in step
 * so a bucket reads the same color on a serve card and a menu-slot tag.
 */
export const MENU_BADGE_COLOR_HEX: Readonly<Record<MenuBadgeColorKey, string>> = Object.freeze({
  red: '#dc2626',
  orange: '#ea580c',
  amber: '#d97706',
  yellow: '#ca8a04',
  green: '#16a34a',
  teal: '#0d9488',
  cyan: '#0891b2',
  blue: '#2563eb',
  indigo: '#4f46e5',
  violet: '#7c3aed',
  purple: '#9333ea',
  pink: '#db2777',
  rose: '#e11d48',
  slate: '#475569',
  gray: '#4b5563',
})

export interface MenuLineStyle {
  /** Ordo light-badge color key for the line's badge/chip tint. */
  colorKey: MenuBadgeColorKey
}

/**
 * One color chip per menu line (no emoji — lines are COLOR-coded; variants
 * are the emoji axis). HALAL keeps the teal used by the Halal filterable tag.
 */
export const MENU_LINE_STYLES: Readonly<Record<MenuLineCode, MenuLineStyle>> = Object.freeze({
  FRESH: { colorKey: 'green' },
  HALAL: { colorKey: 'teal' },
  FAVES: { colorKey: 'amber' },
  BAGGED_TO_GO: { colorKey: 'orange' },
  A_LA_CARTE: { colorKey: 'cyan' },
  BACK_UP: { colorKey: 'indigo' },
  CUSTOM: { colorKey: 'violet' },
  RESTRICTED: { colorKey: 'blue' },
  HOLIDAY_TREAT: { colorKey: 'amber' },
  HOLIDAY_MEAL: { colorKey: 'amber' },
  HOLIDAY_A_LA_CARTE: { colorKey: 'amber' },
})

const FALLBACK_MENU_LINE_STYLE: MenuLineStyle = Object.freeze({ colorKey: 'gray' })

/** Style for a menu-line code; unknown/legacy codes get a neutral fallback. */
export function menuLineStyle(code: string | null | undefined): MenuLineStyle {
  if (code && code in MENU_LINE_STYLES) {
    return MENU_LINE_STYLES[code as MenuLineCode]
  }
  return FALLBACK_MENU_LINE_STYLE
}

/**
 * Plain display label for a menu line — the taxonomy label, or the stored
 * line name when it differs (custom-named lines keep their own name). Menu
 * lines carry NO emoji prefix: chip surfaces tint the label via
 * `menuLineStyle(code).colorKey`; plain-text contexts (select options)
 * render the label alone.
 */
export function menuLineDisplayLabel(code: string | null | undefined, name?: string | null): string {
  return name?.trim() || (code ? (MENU_LINE_LABELS[code as MenuLineCode] ?? code) : '')
}

export interface MenuVariantStyle {
  /** Tasteful marker rendered before the variant label (pickers, filters, chips). */
  emoji: string
}

/**
 * One emoji per menu VARIANT (the regional/tier axis — 🍽️ Standard first).
 * The Record is exhaustive over the taxonomy so a stray key can't linger
 * after a variant rename.
 */
export const MENU_VARIANT_STYLES: Readonly<Record<MenuVariant, MenuVariantStyle>> = Object.freeze({
  STANDARD: { emoji: '🍽️' },
  CALIFORNIA: { emoji: '🐻' },
  PNW: { emoji: '🌲' },
  THE_ROCKIES: { emoji: '🏔️' },
  SOUTHWEST: { emoji: '🌵' },
  SOUTHEAST: { emoji: '🍑' },
  MIDWEST: { emoji: '🌽' },
  NORTHEAST: { emoji: '🍁' },
  HAWAII: { emoji: '🌺' },
  ALASKA: { emoji: '❄️' },
  STANDARD_COL: { emoji: '🍽️' },
  PREMIUM_COL: { emoji: '➕' },
  MAX_COL: { emoji: '❗️' },
})

/** Style for a variant code; unknown/retired codes get no marker. Missing
 * codes read as the STANDARD default (`MENU_VARIANTS[0]`). */
export function menuVariantStyle(code: string | null | undefined): MenuVariantStyle | null {
  const resolved = code == null || code === '' ? 'STANDARD' : code
  return resolved in MENU_VARIANT_STYLES ? MENU_VARIANT_STYLES[resolved as MenuVariant] : null
}

/**
 * Plain variant label ("California", "Premium" for PREMIUM_COL). Use in
 * composed TEXT — template names, preview titles — where an emoji prefix
 * would pollute stored strings. Unknown/retired codes pass through unchanged
 * (callers' formatToken-style fallbacks cover theoretical strays); missing
 * codes read as the STANDARD default.
 */
export function menuVariantLabel(code: string | null | undefined): string {
  const resolved = code == null || code === '' ? 'STANDARD' : code
  return MENU_VARIANT_LABELS[resolved as MenuVariant] ?? resolved
}

/**
 * "🍽️ Standard" / "🐻 California" — emoji-prefixed display label for variant
 * pickers, filter choices and chips. Unknown/retired codes render their
 * label with no emoji.
 */
export function menuVariantDisplayLabel(code: string | null | undefined): string {
  const label = menuVariantLabel(code)
  const style = menuVariantStyle(code)
  return style && label ? `${style.emoji} ${label}` : label
}

/**
 * Ordo light-badge color per dietary VARIANT (scenario need), anchored to the
 * filterable-tag colors these concepts already carry: teal Halal, indigo
 * Kosher, cyan "X-free" claims, violet for the rest.
 */
export const MENU_DIETARY_NEED_COLOR_KEYS: Readonly<Record<MenuDietaryNeed, MenuBadgeColorKey>> = Object.freeze({
  HALAL: 'teal',
  KOSHER: 'indigo',
  PORK_FREE: 'cyan',
  BEEF_FREE: 'cyan',
  OBSERVES_LENT: 'violet',
})

/** Badge color for a dietary-need code; unknown codes read as custom (violet). */
export function menuDietaryNeedColorKey(code: string | null | undefined): MenuBadgeColorKey {
  if (code && code in MENU_DIETARY_NEED_COLOR_KEYS) {
    return MENU_DIETARY_NEED_COLOR_KEYS[code as MenuDietaryNeed]
  }
  return 'violet'
}

/**
 * ONE color per menu BUCKET (PROD-1304) — the meal option a student ordered,
 * so a cafeteria worker reads the tray from the color before the name. The
 * nine prescribed buckets each take a DIFFERENT hue family so no two meal
 * options on the same line share a tint: purple Main (product decision
 * 2026-09-18), green Veg (the vegetarian green every Ordo surface uses),
 * cyan cold, blue GF/DF, pink DF/EF, amber Best Seller, orange Cereal,
 * red à-la-carte Side, slate Beverage. Prescribed codes never fall back.
 */
export const MENU_BUCKET_COLOR_KEYS: Readonly<Record<MenuBucketCode, MenuBadgeColorKey>> = Object.freeze({
  MAIN: 'purple',
  VEG: 'green',
  COLD_SANDO_SALAD: 'cyan',
  GF_DF: 'blue',
  DF_EF: 'pink',
  BEST_SELLER: 'amber',
  CEREAL: 'orange',
  SIDE: 'red',
  BEVERAGE: 'slate',
})

/**
 * Colors for the user-named buckets on configurable lines (`OPTION_1..N`,
 * `isCustom`), in the order they are assigned. Ordered for maximum
 * differentiation between NEIGHBOURS — a two-option line is purple vs green,
 * three adds orange — with the closest tones (indigo/blue, rose/pink,
 * teal/cyan, yellow/amber) pushed to the tail where lines rarely reach.
 * Option 1 mirrors Main and 2 mirrors Veg on purpose: a custom line's first
 * two options are almost always the meat and veg specials under other names.
 */
export const CUSTOM_BUCKET_OPTION_COLOR_KEYS: readonly MenuBadgeColorKey[] = Object.freeze([
  'purple',
  'green',
  'orange',
  'blue',
  'pink',
  'amber',
  'teal',
  'red',
  'indigo',
  'rose',
  'cyan',
  'yellow',
])

const CUSTOM_BUCKET_CODE_PATTERN = /^OPTION_(\d+)$/u
const FALLBACK_MENU_BUCKET_COLOR_KEY: MenuBadgeColorKey = 'gray'

/** `OPTION_7` → 7; null for any other code. */
export function customBucketOptionIndex(code: string | null | undefined): number | null {
  const match = code ? CUSTOM_BUCKET_CODE_PATTERN.exec(code) : null
  if (!match) {
    return null
  }
  const index = Number.parseInt(match[1]!, 10)
  return index >= 1 ? index : null
}

/**
 * Badge color for a bucket code — prescribed codes from
 * `MENU_BUCKET_COLOR_KEYS`, `OPTION_n` from the custom sequence (1-based;
 * past the sequence it wraps so a 13th option is still tinted, never gray),
 * anything else (legacy/unknown) neutral gray.
 */
export function menuBucketColorKey(code: string | null | undefined): MenuBadgeColorKey {
  if (code && code in MENU_BUCKET_COLOR_KEYS) {
    return MENU_BUCKET_COLOR_KEYS[code as MenuBucketCode]
  }
  const optionIndex = customBucketOptionIndex(code)
  if (optionIndex !== null) {
    return CUSTOM_BUCKET_OPTION_COLOR_KEYS[(optionIndex - 1) % CUSTOM_BUCKET_OPTION_COLOR_KEYS.length]!
  }
  return FALLBACK_MENU_BUCKET_COLOR_KEY
}

/** Solid hex for a bucket code — `menuBucketColorKey` resolved through `MENU_BADGE_COLOR_HEX`. */
export function menuBucketColorHex(code: string | null | undefined): string {
  return MENU_BADGE_COLOR_HEX[menuBucketColorKey(code)]
}

// Label → code resolution for surfaces that only carry human labels (preview
// payloads, pricing dietary-variant rows). Covers the canonical taxonomy
// labels plus the label spellings in active use ("No Pork" setup chips,
// "Pork-free" pricing rows).
const DIETARY_NEED_CODES_BY_NORMALIZED_LABEL: Readonly<Record<string, MenuDietaryNeed>> = Object.freeze({
  ...Object.fromEntries(
    (Object.entries(MENU_DIETARY_NEED_LABELS) as Array<[MenuDietaryNeed, string]>).map(([code, label]) => [
      normalizeDietaryLabel(label),
      code,
    ]),
  ),
  [normalizeDietaryLabel('No Pork')]: 'PORK_FREE',
  [normalizeDietaryLabel('No Beef')]: 'BEEF_FREE',
})

function normalizeDietaryLabel(label: string): string {
  return label.trim().toLowerCase().replace(/[-_]/gu, ' ').replace(/\s+/gu, ' ')
}

/** Dietary-need code for a human label ("Pork-free" → PORK_FREE); null when unrecognized. */
export function menuDietaryNeedCodeForLabel(label: string | null | undefined): MenuDietaryNeed | null {
  if (!label) {
    return null
  }
  return DIETARY_NEED_CODES_BY_NORMALIZED_LABEL[normalizeDietaryLabel(label)] ?? null
}

/** Badge color for a dietary-variant LABEL; unrecognized labels read as custom (violet). */
export function menuDietaryNeedColorKeyForLabel(label: string | null | undefined): MenuBadgeColorKey {
  return menuDietaryNeedColorKey(menuDietaryNeedCodeForLabel(label))
}
